import { Activity } from "lucide-react";
import { Operation, WorkflowRunStatus, gql } from "types";
import { FieldType, InternalResourceConfig } from "../lib/internal-resources";
import { WorkflowRunDetailsDrawer } from "../lib/internal-resources/components/WorkflowRunDetailsDrawer";
import { WorkflowRunStatusTag } from "../lib/internal-resources/components/WorkflowRunStatusTag";
import { renderField } from "../lib/internal-resources/field-renderers";

const GET_WORKFLOW_RUNS_LIST = gql(/* GraphQL */ `
  query InternalWorkflowRunList(
    $pagination: Pagination
    $filters: FiltersExpression
    $orderBy: InternalWorkflowRunOrderByInput
  ) {
    internalWorkflowRunList(
      pagination: $pagination
      filters: $filters
      orderBy: $orderBy
    ) {
      count
      list {
        id
        status
        workflowVersionId
        workflowName
        lastNodeId
        createdAt
        input
        subject {
          type
          id
          name
        }
        workflowVersion {
          version
          workflowDefinition {
            name
          }
        }
      }
    }
  }
`);

const SCOPE_LABELS: Record<string, string> = {
  Employee: "Employee",
  Company: "Company",
  ProductInstance: "Product instance",
};

export const workflowRunResourceConfig: InternalResourceConfig = {
  resourceName: "workflow-run",
  displayNameKey: "apps.admin.internalResources.workflowRun.title",
  basePath: "/internal/data/workflow-runs",

  listQuery: GET_WORKFLOW_RUNS_LIST,
  listQueryName: "internalWorkflowRunList",

  fields: [
    {
      key: "id",
      labelKey: "apps.admin.internalResources.workflowRun.columns.id",
      type: FieldType.Uuid,
      width: 190,
    },
    {
      key: "workflowName",
      labelKey: "apps.admin.internalResources.workflowRun.columns.workflowName",
      type: FieldType.String,
      width: 220,
      format: (value, item) => {
        const dbName = (
          item as {
            workflowVersion?: {
              workflowDefinition?: { name?: string | null } | null;
            } | null;
          }
        )?.workflowVersion?.workflowDefinition?.name;
        return renderField(FieldType.String, value ?? dbName ?? null, item);
      },
    },
    {
      key: "workflowVersion.version",
      labelKey: "apps.admin.internalResources.workflowRun.columns.version",
      type: FieldType.String,
      width: 110,
      format: (value) =>
        value === null || value === undefined ? "-" : `v${String(value)}`,
    },
    {
      key: "subject.type",
      labelKey: "apps.admin.internalResources.workflowRun.columns.scope",
      type: FieldType.String,
      width: 150,
      format: (value) => SCOPE_LABELS[String(value)] ?? "-",
    },
    {
      key: "subject.name",
      labelKey: "apps.admin.internalResources.workflowRun.columns.scopedTo",
      type: FieldType.String,
      width: 200,
    },
    {
      key: "status",
      labelKey: "apps.admin.internalResources.workflowRun.columns.status",
      type: FieldType.Enum,
      width: 130,
      sortKey: "Status",
      format: (value) => (
        <WorkflowRunStatusTag status={value as WorkflowRunStatus} />
      ),
    },
    {
      key: "lastNodeId",
      labelKey: "apps.admin.internalResources.workflowRun.columns.lastNode",
      type: FieldType.String,
      width: 160,
    },
    {
      key: "createdAt",
      labelKey: "apps.admin.internalResources.workflowRun.columns.createdAt",
      type: FieldType.DateTime,
      width: 180,
      sortKey: "CreatedAt",
    },
  ],

  list: {
    defaultSort: { column: "CreatedAt", direction: "desc" },
    searchFields: [
      { field: "workflowRun.id", op: Operation.Eq },
      "workflowRun.status",
    ],
  },

  details: {
    component: WorkflowRunDetailsDrawer,
    paramKey: "runId",
  },

  navigation: {
    icon: <Activity size={16} />,
    order: 8,
  },
};
